import { horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  type Cell,
  flexRender,
  type Header,
  type HeaderGroup,
  type Table as TableType,
  type RowData,
} from "@tanstack/react-table";
import { AlertCircle, ChevronDown, ChevronsUpDown, ChevronUp, GripVertical, Pin, PinOff } from "lucide-react";
import { memo, type CSSProperties, type ReactNode, useMemo } from "react";
import { useSpinDelay } from "spin-delay";
import { useTableContext } from "./TableContext";
import { EmptyState } from "@/components/EmptyState";
import { Tooltip } from "@/components/Tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody as BaseTableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils/common";

const CELL_HEIGHT = 40 as const;

interface TableBodyProps<TData extends RowData> {
  onRowClick?: (row: TData) => void;
  emptyState?: ReactNode;
  pageSize: number;
}

export function TableBody<TData extends RowData>({ onRowClick, emptyState, pageSize }: TableBodyProps<TData>) {
  const { table, loading, error } = useTableContext<TData>();

  const { getFlatHeaders } = table;
  const columnSizingInfo = table.getState().columnSizingInfo;
  const columnSizing = table.getState().columnSizing;

  const columnSizeVars = useMemo(() => {
    if (columnSizingInfo && columnSizing) {
      const headers = getFlatHeaders();
      const colSizes: { [key: string]: number } = {};
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i]!;
        colSizes[`--header-${header.id}-size`] = header.getSize();
        colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
      }
      return colSizes;
    }
  }, [columnSizing, columnSizingInfo, getFlatHeaders]);

  if (error) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center border-t border-b border-solid border-gray-300 my-4 bg-red-50">
        <ErrorState title="An error occurred" message={error} />
      </div>
    );
  }

  const data = table.options.data;

  return (
    <>
      {!loading && (!data || data.length === 0) ? (
        <div className="flex min-h-[400px] flex-col items-center justify-center border-t border-b border-solid border-gray-300 bg-gray-50">
          {emptyState || (
            <EmptyState
              title="No data available"
              description="Try adjusting your search or filters."
              animationPath="/animations/searching.json"
              size={250}
            />
          )}
        </div>
      ) : (
        <div className="max-h-[600px] overflow-auto w-full">
          <Table
            style={columnSizeVars}
            className="[&_tr_th:first-child]:border-l [&_tr_td:first-child]:border-l border-separate border-spacing-0 w-max min-w-full"
          >
            <TableHeader className="sticky top-0 bg-table-header z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderContent key={headerGroup.id} headerGroup={headerGroup} />
              ))}
            </TableHeader>
            {/* 
              WORKAROUND: React 19 + React Compiler + TanStack Table v8 compatibility issue
              
              Ideally we should always render MemoizedTableBody for performance, but there's a bug
              where prev.table.options.data === next.table.options.data returns true even when 
              the data content has actually changed (different items, pagination, etc.).
              
              This prevents proper re-rendering during normal table operations like pagination,
              sorting, and filtering.
              
              See: https://github.com/TanStack/table/issues/5567
              
              Current workaround:
              - Use MemoizedTableBody only during column resizing (where performance matters most)
              - Use TableBodyContent for normal operations (where data changes frequently)
              
              TODO: Remove this workaround once TanStack Table fixes React 19 + React Compiler compatibility
            */}
            {table.getState().columnSizingInfo.isResizingColumn ? (
              <MemoizedTableBody table={table as TableType<any>} pageSize={pageSize} />
            ) : (
              <TableBodyContent table={table} pageSize={pageSize} onRowClick={onRowClick} />
            )}
          </Table>
        </div>
      )}
    </>
  );
}

const SKELETON_SIZES = {
  row: `h-[${CELL_HEIGHT}px]`,
  cell: { height: "h-5", width: "w-[80%]" },
} as const;

function TableRowSkeleton({ columns }: { columns: number }) {
  return (
    <TableRow className={SKELETON_SIZES.row} data-testid="table-row-skeleton">
      {Array.from({ length: columns }).map((_, i) => (
        <TableCell className="border-r border-b" key={i}>
          <Skeleton className={cn(SKELETON_SIZES.cell.height, SKELETON_SIZES.cell.width)} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function TableBodyLoading({ pageSize, columns }: { pageSize: number; columns: number }) {
  return (
    <BaseTableBody data-testid="table-loading-body">
      {Array.from({ length: pageSize }).map((_, index) => (
        <TableRowSkeleton key={index} columns={columns} />
      ))}
    </BaseTableBody>
  );
}

const TableBodyContent = <TData extends RowData>({
  table,
  onRowClick,
  pageSize,
}: {
  table: TableType<TData>;
  onRowClick?: (row: TData) => void;
  pageSize: number;
}) => {
  const { columnOrder, loading } = useTableContext<TData>();
  const showSkeletonLoader = useSpinDelay(loading, {
    delay: 300,
    minDuration: 350,
  });

  const isInitialLoad = loading && !table.getRowModel().rows.length;

  if (showSkeletonLoader || isInitialLoad) {
    return <TableBodyLoading pageSize={pageSize} columns={table.getVisibleFlatColumns().length} />;
  }

  const rows = table.getRowModel().rows;

  return (
    <BaseTableBody className="[&_tr:last-child]:border-1">
      {rows.map((row) => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && "selected"}
          className={onRowClick ? "cursor-pointer" : "cursor-auto"}
          onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        >
          {row.getVisibleCells().map((cell) => (
            <SortableContext key={cell.id} items={columnOrder} strategy={horizontalListSortingStrategy}>
              <DragAlongCell key={cell.id} cell={cell} />
            </SortableContext>
          ))}
        </TableRow>
      ))}
    </BaseTableBody>
  );
};

const MemoizedTableBody = memo(
  TableBodyContent,
  <TData extends RowData>(
    prev: {
      table: TableType<TData>;
      pageSize: number;
      onRowClick?: (row: TData) => void;
    },
    next: {
      table: TableType<TData>;
      pageSize: number;
      onRowClick?: (row: TData) => void;
    },
  ) => {
    const result = {
      dataEqual: prev.table.options.data === next.table.options.data,
      pageSizeEqual: prev.pageSize === next.pageSize,
      onRowClickEqual: prev.onRowClick === next.onRowClick,
    };

    return Object.values(result).every(Boolean);
  },
);

function TableHeaderContent<TData>({ headerGroup }: { headerGroup: HeaderGroup<TData> }) {
  const { columnOrder, loading } = useTableContext<TData>();
  return (
    <TableRow key={headerGroup.id} className={cn(loading && "opacity-60 pointer-events-none")}>
      <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
        {headerGroup.headers.map((header) => (
          <DraggableTableHeader key={header.id} header={header} disabled={loading} />
        ))}
      </SortableContext>
    </TableRow>
  );
}

const DraggableTableHeader = <TData extends RowData>({
  header,
  disabled,
}: {
  header: Header<TData, any>;
  disabled: boolean;
}) => {
  const { onColumnPinningChange, columnOrder } = useTableContext<TData>();
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
  });

  const isFirstColumn = columnOrder[0] === header.column.id;
  const isPinned = header.column.getIsPinned() === "left";
  const leftOffset = header.column.getStart("left");

  const handlePinToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPinned) {
      // Unpin
      onColumnPinningChange({ left: [], right: [] });
    } else {
      // Pin the first column
      onColumnPinningChange({ left: [header.column.id], right: [] });
    }
  };

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    transform: CSS.Translate.toString(transform),
    whiteSpace: "nowrap",
    width: `calc(var(--header-${header.id}-size) * 1px)`,
    zIndex: isDragging ? 1 : isPinned ? 30 : 0,
    ...(isPinned && {
      position: "sticky",
      left: `${leftOffset}px`,
      backgroundColor: "hsl(var(--table-header-background))",
    }),
  };

  return (
    <TableHead
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative overflow-hidden",
        "border-r border-t border-b text-foreground",
        "[&.resizing]:select-none",
        header.column.getIsResizing() && "resizing",
        disabled && "cursor-default",
        isPinned && "shadow-[2px_0_4px_rgba(0,0,0,0.1)]",
      )}
    >
      {disabled ? null : (
        <Tooltip asChild label="Drag to reorder">
          <button
            className="absolute left-1 top-3 w-4 opacity-0 group-hover:opacity-100"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 hover:cursor-grab" />
          </button>
        </Tooltip>
      )}
      {isFirstColumn && !disabled && !isDragging && (
        <Tooltip asChild label={isPinned ? "Unpin column" : "Pin column"}>
          <button
            onClick={handlePinToggle}
            className="absolute right-8 top-3 w-4 h-4 z-30 pointer-events-auto opacity-0 group-hover:opacity-100"
            type="button"
          >
            {isPinned ? <Pin className="h-4 w-4 fill-current" /> : <PinOff className="h-4 w-4" />}
          </button>
        </Tooltip>
      )}
      <div
        onClick={disabled ? undefined : header.column.getToggleSortingHandler()}
        className={cn(
          "transition-transform duration-200",
          // Default state: All headers at x-0, with hover effect at x-3 when not loading
          disabled ? "translate-x-0" : ["translate-x-0", "group-hover:translate-x-3"],
          // Enable pointer cursor for sortable columns when not loading
          !disabled && header.column.getCanSort() && "cursor-pointer",
          // Keep sorted column pushed right (x-3) during loading to prevent visual jumping
          disabled && header.column.getIsSorted() && "translate-x-3",
          "line-clamp-1 text-wrap pr-6",
          isFirstColumn && "group-hover:pr-11",
        )}
      >
        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
      </div>
      {header.column.getCanResize() && (
        <div
          onMouseDown={disabled ? undefined : header.getResizeHandler()}
          onTouchStart={disabled ? undefined : header.getResizeHandler()}
          className={cn(
            "absolute right-0 top-0 z-10 h-full w-1",
            !disabled && "cursor-col-resize group-hover:bg-border",
            header.column.getIsResizing() && "bg-muted-foreground",
          )}
        />
      )}
      {header.column.getCanSort() && (
        <span className="absolute right-2 top-3 pointer-events-none">
          {!header.column.getIsSorted() ? (
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          ) : (
            {
              asc: <ChevronDown className="h-4 w-4" />,
              desc: <ChevronUp className="h-4 w-4" />,
            }[header.column.getIsSorted() as string]
          )}
        </span>
      )}
    </TableHead>
  );
};

const DragAlongCell = ({ cell }: { cell: Cell<any, any> }) => {
  const { loading } = useTableContext();
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const isPinned = cell.column.getIsPinned() === "left";
  const leftOffset = cell.column.getStart("left");

  const style: CSSProperties = {
    opacity: loading ? 0.6 : isDragging ? 0.8 : 1,
    position: isPinned ? "sticky" : "relative",
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
    zIndex: isDragging ? 1 : isPinned ? 5 : 0,
    ...(isPinned && {
      left: `${leftOffset}px`,
      backgroundColor: "hsl(var(--background))",
    }),
  };

  return (
    <TableCell
      style={style}
      className={cn(
        "border-r border-b max-w-[1px]",
        "overflow-hidden text-ellipsis whitespace-nowrap",
        `h-[${CELL_HEIGHT}px]`,
        isPinned && "shadow-[2px_0_4px_rgba(0,0,0,0.1)]",
      )}
      ref={setNodeRef}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );
};

interface ErrorStateProps {
  title?: string;
  message: string;
  className?: string;
}

function ErrorState({ title = "Error", message, className = "" }: ErrorStateProps) {
  return (
    <div data-testid="error-state" className={`flex flex-col items-center justify-center p-4 ${className}`}>
      <div className="flex items-center justify-center gap-2 text-red-500 mb-2">
        <AlertCircle className="h-6 w-6" />
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-red-600">{message}</p>
    </div>
  );
}
